<template>
  <div class="tonal-header-container">
    <h1>
      {{ chartTitle }}:
      <div
        v-if="tonalResult === 'Loading...'"
        class="lds-dual-ring-dashboard"
      ></div>
      <span v-if="tonalResult === 'pass'" class="icon-tonal-pass"
        >&#10004;
      </span>
      <span :style="tonalResultStyle">{{ tonalResult }}</span>
    </h1>
  </div>
  <div class="tonal-chart-wrapper">
    <div class="tonal-noise-container">
      <v-chart v-if="loaded" :option="chartOption" class="chart-container" autoresize />
    </div>
    <div v-if="!loaded" class="whiteout-overlay">
      <img src="@/assets/svg/12-dots-scale-rotate-magecdn.svg" alt="Loading..." class="tonal-spinner" />
    </div>
  </div>
</template>

<script>
import { api_config } from "@/env.ts";
import axios from "axios";
import { use } from "echarts/core";
import VChart from "vue-echarts";
import { getToken } from "../keycloak-wrapper.ts";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart, LineChart } from "echarts/charts";
import { bankersRound } from "../helpers";

import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DatasetComponent,
  AxisPointerComponent,
} from "echarts/components";

use([
  CanvasRenderer,
  BarChart,
  LineChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DatasetComponent,
  AxisPointerComponent,
]);

export default {
  name: "TonalNoise",
  components: {
    VChart,
  },
  props: {
    APIparams: {
      type: Object,
      required: true,
    },
    regions: {
      type: Array,
      required: true,
    },
    sourceNames: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      abortController: null,
      rawChartData: null,
      loaded: false,
      chartOption: {},
      tonalResult: "Loading...",
      tonalResultStyle: "",
    };
  },
  computed: {
    chartTitle() {
      return this.sourceNames?.length > 0
        ? `Tonal Noise (${this.sourceNames.join(', ')})`
        : 'Tonal Noise (Ambient)';
    },
  },
  methods: {
    async fetchData() {
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController()

      this.loaded = false;
      this.tonalResult = "Loading...";
      this.tonalResultStyle = "";
      try {
        const token = await getToken();
        const response = await axios.post(
          api_config.backend + "/tonalNoiseZ",
          {
            unit_ID: this.APIparams.unit_ID,
            date_time: this.APIparams.date_time,
            regions: this.regions
          },
          {
            signal: this.abortController.signal,
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        // Assuming response.data contains keys "spectra" and "thresholds"
        this.rawChartData = response.data;
        this.loaded = true;
        this.updateChart();
      } catch (error) {
        console.error("Error fetching tonal noise data:", error);
      }
    },
    updateChart() {
      if (!this.loaded || !this.rawChartData) return;
      const spectra = this.rawChartData.spectra;
      const tonal_bands = this.rawChartData.tonal_bands;
      const xAxisData = Object.keys(spectra);
      // For each band, take the first sample (if it's an array) or the value directly
      const yData = xAxisData.map((key) => {
        const value = spectra[key];
        return value[0];
      });
      if (this.rawChartData.outcome == "pass") {
        this.tonalResult = "pass";
        this.tonalResultStyle = "color: #009B69;";
      } else {
        this.tonalResult = "fail";
        this.tonalResultStyle = "color: red;";
      }
      this.chartOption = {
        //   title: {
        //     text: "Tonal Noise",
        //   },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          shadowStyle: {
            color: "rgba(0,0,0,0.5)",
          },
          formatter: function (param) {
            const content = `${param[0].name}</b><br/>${
              param[0].marker
            }Spectra: ${bankersRound(param[0].value)}`;
            return content;
          },
        },
        xAxis: {
          type: "category",
          data: xAxisData,
          name: "1/3rd Octave Spectra Band",
        },
        yAxis: {
          type: "value",
          name: "dB",
        },
        grid: {
          left: 30,
          top: 10,
          right: 0,
          bottom: 20,
        },
        series: [
          {
            type: "bar",
            data: yData,
            itemStyle: {
              color: (params) => (tonal_bands.includes(xAxisData[params.dataIndex]) ? "red" : "#009B69"),
              opacity: 0.6,
            },
            name: "Spectra",
          },
        ],
      };
    },
  },
  watch: {
    APIparams: {
      deep: true,
      handler() {
        this.fetchData();
      },
    },
    regions: {
      deep: true,
      handler() {
        this.fetchData();
      },
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
.tonal-header-container {
  height: 40px;
  z-index: 5;
}

.tonal-noise-container {
  padding: 0px;
  background-color: var(--colour-bg-light);
  height: 100%;
  width: 100%;
}

.chart-container {
  width: 100%;
  height: 100%;
}

.tonal-chart-wrapper {
  position: relative;
  flex: 1;
  height: 100%;
}

.tonal-spinner {
  width: 40%;
  height: 40%;
}

.icon-tonal-pass {
  color: var(--color-primary);
  font-size: 0.7em;
}
</style>
